import { Container } from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { type ReelAnticipationFrame } from "@slot/mechanics-reels";
import type { GameVisualAssets } from "../assets/GameVisualAssets";
import { CharacterView } from "../views/character/CharacterView";
import { SavedTotalView } from "../features/saved-total/SavedTotalView";
import { SYMBOL_CATALOG } from "../assets/symbols/symbolCatalog";
import { RuntimeSymbolCatalog } from "../assets/symbols/RuntimeSymbolCatalog";
import { AnticipationFrameView } from "../presentation/AnticipationFrameView";
import { AnticipationBackground } from "../presentation/AnticipationBackground";
import { createWinAmountLabelFactory } from "../composition/createWinAmountLabel";
import { GameBackground } from "./GameBackground";
import { GridContainer } from "./GridContainer";
import { fitWithin } from "@slot/presentation";
import type { ContentAreaStore, FitRule, Log, PresentationTaskRunner, SpineDriver, ViewportStore, WinAmountLabelFactory } from "@slot/presentation";
import type { FeatureStateStore, GameLogoSpec } from "@slot/game-shell";

/**
 * The composition root: 1080×1067.356 authored (`GameCompositionRoot`, scale-mode window layout, desktop max width 1400,
 * mobile 1100, lifted 100 on desktop and portrait). Every shape fits that box into the content area: the grid frame's
 * wood is that box, its sprite's margin (1217.3×1199.1) overhangs it. Measured against the Cocos build on the three
 * e2e shapes: the same scale, and the same centre within 0.3 px.
 */
export const COMPOSITION_ROOT = {
  width: 1080,
  height: 1067.356,
  fit: {
    box: {
      desktop: { width: 1080, height: 1067.356 },
      mobile: { width: 1080, height: 1067.356 },
      mobile_landscape: { width: 1080, height: 1067.356 },
    },
    limits: { desktop: { maxWidth: 1400 }, mobile: { maxWidth: 1100 }, mobile_landscape: {} },
    offsetY: { desktop: -100, mobile: -100, mobile_landscape: 0 },
  },
} as const satisfies { width: number; height: number; fit: FitRule };

/**
 * `BackdropLayer/BrandedIconLayer` from the scene: the logo's box and sprite, its spot per shape. On desktop the spot
 * sits outside the composition box, left of the grid, and stays there: this scene keeps the logo inside the screen's
 * slot (`desktopVisibleBoundsRoot`), not inside the composition box the shell's `keepInsideX` measures. The screen holds
 * the spot on every desktop window from 4:3 up; a narrower one clips the logo's left edge (plan, deferred list).
 */
export const GAME_LOGO: GameLogoSpec = {
  box: { width: 363.1, height: 254.4 },
  sprite: { width: 363.4, height: 248 },
  placement: {
    desktop: { x: -763.503, y: -453.571 },
    mobile: { x: -326.198, y: -638.563 },
    mobile_landscape: { x: -777.107, y: -361.609, scale: 0.7 },
  },
};

/**
 * The `GameScene` display tree: the background layer, the composition root (the bear behind the grid, the grid
 * container, the HUD, the FX overlays), the fly layer over them, and the per-round factories the composition hands to
 * the round presentation.
 * Mirrors the authored scene node for node; every geometry constant is sourced from the scene dump.
 */
export class GameScene extends Container {
  public readonly background: GameBackground;
  public readonly compositionRoot: Container;
  public readonly gridContainer: GridContainer;
  public readonly hudLayer: Container;
  /** The Super Bonus saved total (hidden outside the session). */
  public readonly savedTotal: SavedTotalView;
  public readonly fxOverlays: Container;
  public readonly symbolCatalog: RuntimeSymbolCatalog;
  public readonly anticipationBackground: AnticipationBackground;
  public readonly createWinAmountLabel: WinAmountLabelFactory | null;
  public readonly createAnticipationFrame: (() => ReelAnticipationFrame) | null;
  /** The bear; null when its skeleton did not load. */
  public readonly characterView: CharacterView | null;
  private readonly disposers: Array<() => void> = [];

  public constructor(input: {
    assets: GameVisualAssets;
    chromeTaskRunner: PresentationTaskRunner;
    featureStateStore: FeatureStateStore;
    viewport: ViewportStore;
    contentArea: ContentAreaStore;
    spineDriver: SpineDriver;
    log: Log;
  }) {
    super({ label: "GameScene" });
    const { assets, log } = input;
    const chromeRunner = input.chromeTaskRunner;

    // ── Background ────────────────────────────────────────────────────────────────────────────
    this.background = new GameBackground({ assets, spineDriver: input.spineDriver, log });
    this.background.bind(input.viewport);
    this.addChild(this.background);
    this.anticipationBackground = new AnticipationBackground({
      overlayTargets: [this.background.overlayTarget],
      chromeRunner,
    });

    // ── Composition root: fitted into the content area per shape ─────────────────────────────
    this.compositionRoot = new Container({ label: "GameCompositionRoot" });
    this.addChild(this.compositionRoot);
    this.disposers.push(
      input.contentArea.subscribe((area) => {
        const { scale, x, y } = fitWithin(COMPOSITION_ROOT.fit, input.viewport.getSnapshot().shape, area);
        this.compositionRoot.scale.set(scale, scale);
        this.compositionRoot.position.set(x, y);
      }),
    );

    // ── The bear, behind the grid (BackdropLayer/CharacterOverlayLayer) ──────────────────────
    this.characterView = assets.skeletons.bear
      ? new CharacterView({ skeletonData: assets.skeletons.bear, viewport: input.viewport, spineDriver: input.spineDriver, runner: chromeRunner })
      : null;
    if (this.characterView) this.compositionRoot.addChild(this.characterView);

    // ── Mechanic layer: the GridContainer prefab ─────────────────────────────────────────────
    const mechanicLayer = new Container({ label: "MechanicLayer" });
    this.gridContainer = new GridContainer({ baseFrame: assets.textures.baseGridFrame, featureFrame: assets.textures.featureGridFrame });
    this.gridContainer.bindFeatureState(input.featureStateStore);
    mechanicLayer.addChild(this.gridContainer);
    this.compositionRoot.addChild(mechanicLayer);

    // FX overlays (the win-amount labels) above the HUD, in composition-root space (MechanicOverlayLayer/FxOverlayHost).
    this.fxOverlays = new Container({ label: "FxOverlays" });

    // The fly layer, above the whole composition in design units: the saved total's value flies from the HUD into a
    // battle's top label through it (Cocos flies it in world space, its arc in design units).
    const flyLayer = new Container({ label: "FlyLayer" });

    // ── HUD layer: the Super Bonus saved total ───────────────────────────────────────────────
    this.hudLayer = new Container({ label: "HudLayer" });
    this.savedTotal = new SavedTotalView({ plate: assets.textures.featureMultiplierPlate, flyLayer });
    this.hudLayer.addChild(this.savedTotal);
    this.compositionRoot.addChild(this.hudLayer, this.fxOverlays);
    this.addChild(flyLayer);

    // ── Symbols ──────────────────────────────────────────────────────────────────────────────
    this.symbolCatalog = RuntimeSymbolCatalog.fromConfig({ entries: SYMBOL_CATALOG, atlas: assets.symbols, log });

    // ── Per-round factories ──────────────────────────────────────────────────────────────────
    const woodenNumbers = assets.fonts.woodenNumbers;
    this.createWinAmountLabel = woodenNumbers ? createWinAmountLabelFactory({ font: woodenNumbers }) : null;
    if (!this.createWinAmountLabel) log("[GameScene] wooden-numbers font not loaded — no win-amount labels");

    const frameSkeleton = assets.skeletons.anticipationFrame;
    this.createAnticipationFrame = frameSkeleton
      ? () => {
          const spine = new Spine({ skeletonData: frameSkeleton, autoUpdate: false });
          spine.label = "AnticipationFrameSpine";
          input.spineDriver.attach(spine);
          return new AnticipationFrameView({ spine, chromeRunner });
        }
      : null;
  }

  public dispose(): void {
    this.characterView?.dispose();
    for (const d of this.disposers) d();
    this.gridContainer.dispose();
    this.background.dispose();
    this.destroy({ children: true });
  }
}
